import React, { useState } from 'react';
import { Icon } from '../../Icon';
import { parseBrazilianNumber } from '../../../utils/formatUtils';
import { FormField } from './shared';

const InterestCalculator = ({ type, onResultChange }: { type: 'simple' | 'compound', onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [c, setC] = useState('1.000');
  const [i, setI] = useState('1');
  const [t, setT] = useState('12');
  const [result, setResult] = useState<any>(null);

  const calculate = () => {
    const capital = parseFloat(parseBrazilianNumber(c)) || 0;
    const taxa = parseFloat(parseBrazilianNumber(i)) / 100;
    const tempo = parseFloat(parseBrazilianNumber(t)) || 0;

    if (capital <= 0 || tempo <= 0 || !isFinite(taxa) || isNaN(taxa) || taxa < 0) {
      return;
    }

    let res;
    if (type === 'simple') {
      const juros = capital * taxa * tempo;
      res = { total: capital + juros, juros };
    } else {
      const total = capital * Math.pow(1 + taxa, tempo);
      res = { total, juros: total - capital };
    }

    setResult(res);

    onResultChange([
      { label: 'Capital Inicial', value: `R$ ${capital.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Taxa Mensal', value: `${i}%` },
      { label: 'Tempo (meses)', value: `${t}` },
      { label: 'Total Juros', value: `R$ ${res.juros.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Valor Total', value: `R$ ${res.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` }
    ]);
  };

  return (
    <div className="space-y-8">
      <div className="bg-theme-result p-6 rounded-2xl border border-theme-border">
        <h2 className="text-xl font-bold text-theme-text mb-3">
          Juros {type === 'simple' ? 'Simples' : 'Compostos'}
        </h2>
        <p className="text-theme-muted">
          {type === 'simple'
            ? 'Cálculo linear de juros sobre o capital inicial.'
            : 'Cálculo de juros compostos (juros sobre juros).'}
        </p>
      </div>

      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        <h3 className="text-lg font-bold text-theme-text mb-6">Dados para Cálculo</h3>

        <FormField
          label="Capital (R$)"
          placeholder="Ex: 1.000"
          value={c}
          onChange={setC}
          required
          isNumeric
        />

        <FormField
          label="Taxa ao mês (%)"
          placeholder="Ex: 1"
          value={i}
          onChange={setI}
          required
          isNumeric
        />

        <FormField
          label="Tempo (meses)"
          placeholder="Ex: 12"
          value={t}
          onChange={setT}
          required
          isNumeric
        />
      </div>

      <div className="mt-8 flex justify-center">
        <button
          onClick={calculate}
          className="px-12 py-5 bg-accent-gradient text-white rounded-[2rem] font-black shadow-accent hover:shadow-accent-hover hover:scale-[1.02] transition-all hover:-translate-y-1 active:scale-95 uppercase text-sm tracking-widest flex items-center gap-3"
        >
          <Icon name="play" className="w-5 h-5" />
          Calcular Juros
        </button>
      </div>
      {result ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-8 rounded-[2.5rem] shadow-xl text-center bg-accent-gradient text-white shadow-accent">
            <p className="text-xs font-black opacity-60 uppercase tracking-widest mb-2">Total</p>
            <p className="text-4xl font-[1000] mb-3">
              R$ {result.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-sm opacity-90">Valor final</p>
          </div>

          <div className="p-8 rounded-[2.5rem] shadow-xl text-center bg-emerald-600 text-white shadow-emerald-200">
            <p className="text-xs font-black opacity-60 uppercase tracking-widest mb-2">Juros</p>
            <p className="text-4xl font-[1000] mb-3">
              R$ {result.juros.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-sm opacity-90">{type === 'simple' ? 'Linear' : 'Composto'}</p>
          </div>

          <div className="p-8 rounded-[2.5rem] shadow-xl text-center bg-slate-600 text-white shadow-slate-200">
            <p className="text-xs font-black opacity-60 uppercase tracking-widest mb-2">Rendimento</p>
            <p className="text-4xl font-[1000] mb-3">
              {(parseFloat(parseBrazilianNumber(c)) > 0 ? (result.juros / parseFloat(parseBrazilianNumber(c))) * 100 : 0).toFixed(1)}%
            </p>
            <p className="text-sm opacity-90">Sobre o capital</p>
          </div>
        </div>
      ) : (
        <div className="text-center py-20 bg-theme-result rounded-3xl border-2 border-dashed border-theme-border">
          <div className="w-20 h-20 bg-theme-input rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="trending-up" className="w-10 h-10 text-slate-300" />
          </div>
          <p className="text-theme-muted max-w-md mx-auto">Informe capital, taxa e tempo e clique em Calcular.</p>
        </div>
      )}
    </div>
  );
};

export default InterestCalculator;
